import React, { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { Entity } from '../types';
import { useSubdomain } from '../hooks/useSubdomain';
import { getEntityBySubdomain } from '../services/entityService';

interface TenantContextType {
  currentTenant: Entity | null;
  isLoading: boolean;
  error: string | null;
}

const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
  children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
  const { subdomain, isMainDomain } = useSubdomain();
  const [currentTenant, setCurrentTenant] = useState<Entity | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchTenant = async () => {
      console.log('[TenantContext] Starting fetchTenant...', { subdomain, isMainDomain });
      setIsLoading(true);
      setError(null);

      try {
        if (isMainDomain) {
          console.log('[TenantContext] Is main domain, no tenant to fetch');
          setCurrentTenant(null);
        } else if (subdomain) {
          console.log('[TenantContext] Fetching tenant for subdomain:', subdomain);
          // Fetch tenant from Supabase
          const tenant = await getEntityBySubdomain(subdomain);
          console.log('[TenantContext] Tenant fetched:', tenant);

          if (tenant) {
            setCurrentTenant(tenant);
            setError(null);
            console.log('[TenantContext] Tenant set successfully:', tenant.name);
          } else {
            // Subdomain not found in entities table — treat as main domain
            console.log('[TenantContext] No entity for subdomain:', subdomain, '- treating as main domain');
            setCurrentTenant(null);
          }
        } else {
          console.log('[TenantContext] No subdomain provided');
          setError('No tenant specified');
          setCurrentTenant(null);
        }
      } catch (err) {
        console.error('[TenantContext] Error loading tenant:', err);
        setError('Failed to load tenant. Please try again.');
        setCurrentTenant(null);
      } finally {
        console.log('[TenantContext] Loading complete, isLoading set to false');
        setIsLoading(false);
      }
    };

    fetchTenant();
  }, [subdomain, isMainDomain]);

  return (
    <TenantContext.Provider value={{ currentTenant, isLoading, error }}>
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (context === undefined) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
};